/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect, useState } from 'react'
import { useParams, Link, useHistory } from 'react-router-dom'
import { AppContext } from '../context/AppContext'
import PaymentReceiveService from '../services/PaymentReceiveService'
 import { getAxiosConfig } from '../helpers/Fetchwrapper'
import axios from 'axios'
import Swal from 'sweetalert2'

const PaymentReceiveCreate = () => {
    const { currentUser } = useContext(AppContext)
    const { orderId } = useParams()
    const history = useHistory()
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [orderDetails, setOrderDetails] = useState(null)
    const [formData, setFormData] = useState({
        orderId: orderId || '',
        invoiceId: '',
        amount: '',
        paymentType: '',
        paymentCategory: 'Regular',
        modeOfTransaction: '',
        transactionId: '',
        notes: '',
        status: 'approved',
        file: null
    })
    const [errors, setErrors] = useState({})
    const [fileError, setFileError] = useState('')

    useEffect(() => {
        if (orderId) {
            fetchOrderDetails()
        }
    }, [orderId])

    const fetchOrderDetails = async () => {
        setLoading(true)
        try {
            const response = await PaymentReceiveService.getOrderForPaymentReceive(orderId)
            if (response.success) {
                setOrderDetails(response.data)
                setFormData(prev => ({
                    ...prev,
                    orderId: orderId,
                    amount: response.data.paymentSummary.remainingAmount > 0 ? 
                        response.data.paymentSummary.remainingAmount.toString() : ''
                }))
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.errorMessage || 'Failed to fetch order details',
                    confirmButtonColor: '#d33'
                })
                history.push('/payments/receive')
            }
        } catch (error) {
            console.error('Error fetching order details:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error fetching order details',
                confirmButtonColor: '#d33'
            })
            history.push('/payments/receive')
        } finally {
            setLoading(false)
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }))
        }
    }

    const handleFileChange = (e) => {
        const file = e.target.files[0]
        if (file) {
            const fileErrors = PaymentReceiveService.validateFileUpload(file)
            if (fileErrors.length > 0) {
                setFileError(fileErrors[0])
                e.target.value = ''
                return
            }
            setFileError('')
            setFormData(prev => ({
                ...prev,
                file: file
            }))
        }
    }

    const validateForm = () => {
        const newErrors = {}
        
        if (!formData.orderId) {
            newErrors.orderId = 'Order ID is required'
        }
        
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Amount must be greater than 0'
        }
        
        if (!formData.paymentType) {
            newErrors.paymentType = 'Payment type is required'
        }

        // Check if amount exceeds remaining amount
        if (orderDetails && formData.amount) {
            const amount = parseFloat(formData.amount)
            const remaining = orderDetails.paymentSummary.remainingAmount
            if (amount > remaining) {
                newErrors.amount = `Amount cannot exceed remaining amount (${PaymentReceiveService.formatCurrency(remaining)})`
            }
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }

        setSubmitting(true)
        try {
            // Create FormData for file upload like ProductList.js
            const submitFormData = new FormData()
            
            // Add all form fields to FormData with proper data types
            submitFormData.append('orderId', parseInt(formData.orderId) || 0)
            submitFormData.append('invoiceId', formData.invoiceId || '')
            submitFormData.append('amount', parseFloat(formData.amount) || 0)
            submitFormData.append('paymentType', formData.paymentType || '')
            submitFormData.append('paymentCategory', formData.paymentCategory || 'Regular')
            submitFormData.append('modeOfTransaction', formData.modeOfTransaction || '')
            submitFormData.append('transactionId', formData.transactionId || '')
            submitFormData.append('notes', formData.notes || '')
            submitFormData.append('status', formData.status || 'approved')
            
            // Add file if present
            if (formData.file) {
                submitFormData.append('file', formData.file)
            }

            // Use axios like ProductList.js
            const config = getAxiosConfig()
            config.headers['Content-Type'] = 'multipart/form-data'
            
            const response = await axios.post(
                process.env.REACT_APP_API_URL + 'payments/receive/create',
                submitFormData,
                config
            )

            const result = response.data

            if (result.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Payment receive created successfully',
                    confirmButtonColor: '#3085d6',
                    timer: 2000,
                    showConfirmButton: false
                })
                history.push('/payments/receive')
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.errorMessage || 'Failed to create payment receive',
                    confirmButtonColor: '#d33'
                })
            }
        } catch (error) {
            console.error('Error creating payment receive:', error)
            
            // Handle axios error response like ProductList.js
            if (error.response && error.response.data) {
                const errorData = error.response.data
                if (errorData.errorMessage && typeof errorData.errorMessage === 'object') {
                    const errorMessages = Object.values(errorData.errorMessage)
                        .flat()
                        .join('\n')
                    
                    Swal.fire({
                        icon: 'error',
                        title: 'Validation Error!',
                        text: errorMessages,
                        confirmButtonColor: '#d33'
                    })
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: errorData.errorMessage || 'Error creating payment receive',
                        confirmButtonColor: '#d33'
                    })
                }
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error creating payment receive',
                    confirmButtonColor: '#d33'
                })
            }
        } finally {
            setSubmitting(false)
        }
    }

    const getPaymentTypes = () => PaymentReceiveService.getPaymentTypes()
    const getTransactionModes = () => [
        { value: 'bKash', label: 'bKash' },
        { value: 'BEFTN', label: 'BEFTN' },
        { value: 'NPSB', label: 'NPSB' },
        { value: 'Fund Transfer', label: 'Fund Transfer' },
        { value: 'Cash Deposit', label: 'Cash Deposit' },
        { value: 'Check Deposit', label: 'Check Deposit' },
        { value: 'RTGS', label: 'RTGS' }
    ]

    if (loading) {
        return (
            <div className='card card-custom'>
                <div className='card-body text-center py-5'>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading order details...</p>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className='card card-custom'>
                <div className='card-header card-header-mobile'>
                    <div className='card-title'>
                        <h3 className='card-label'>Receive Payment</h3>
                    </div>
                    <div className='card-toolbar card-toolbar-mobile'>
                        <Link to="/payments/receive" className='btn btn-secondary btn-mobile'>
                            <i className="fas fa-arrow-left me-2"></i>
                            <span className="btn-text">Back to List</span>
                        </Link>
                    </div>
                </div>

                <div className='card-body'>
                    {/* Order Summary */}
                    {orderDetails && (
                        <div className='order-summary-section mb-4'>
                            <h4 className='section-title'>
                                <i className='fas fa-shopping-cart me-2 text-success'></i>
                                Order Summary
                            </h4>
                            
                            <div className='row'>
                                <div className='col-md-6'>
                                    <div className='summary-card'>
                                        <div className='summary-card-header'>
                                            <i className='fas fa-file-invoice text-primary'></i>
                                            <h5>Order Information</h5>
                                        </div>
                                        <div className='summary-card-body'>
                                            <div className='summary-item'>
                                                <span className='summary-label'>Order No:</span>
                                                <span className='summary-value'>{orderDetails.order.orderNo}</span>
                                            </div>
                                            <div className='summary-item'>
                                                <span className='summary-label'>Order Date:</span>
                                                <span className='summary-value'>{orderDetails.order.formattedOrderDate}</span>
                                            </div>
                                            <div className='summary-item'>
                                                <span className='summary-label'>Order Status:</span>
                                                <span className='summary-value'>
                                                    <span className='badge bg-secondary'>{orderDetails.order.orderStatus}</span>
                                                </span>
                                            </div>
                                            <div className='summary-item'>
                                                <span className='summary-label'>Grand Total:</span>
                                                <span className='summary-value fw-bold text-success'>
                                                    {orderDetails.order.formattedGrandTotal}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className='col-md-6'>
                                    <div className='summary-card'>
                                        <div className='summary-card-header'>
                                            <i className='fas fa-user text-info'></i>
                                            <h5>Customer Information</h5>
                                        </div>
                                        <div className='summary-card-body'>
                                            <div className='summary-item'>
                                                <span className='summary-label'>Customer:</span>
                                                <span className='summary-value'>{orderDetails.customer.customerName}</span>
                                            </div>
                                            <div className='summary-item'>
                                                <span className='summary-label'>Email:</span>
                                                <span className='summary-value'>{orderDetails.customer.email}</span>
                                            </div>
                                            <div className='summary-item'>
                                                <span className='summary-label'>Mobile:</span>
                                                <span className='summary-value'>{orderDetails.customer.mobileNumber}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='row mt-3'>
                                <div className='col-12'>
                                    <div className='payment-summary-card'>
                                        <div className='payment-summary-header'>
                                            <i className='fas fa-money-bill-wave text-warning'></i>
                                            <h5>Payment Summary</h5>
                                        </div>
                                        <div className='payment-summary-body'>
                                            <div className='row'>
                                                <div className='col-md-3'>
                                                    <div className='payment-summary-item'>
                                                        <span className='payment-summary-label'>Total Paid</span>
                                                        <span className='payment-summary-value text-success'>
                                                            {orderDetails.paymentSummary.formattedTotalPaid}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className='col-md-3'>
                                                    <div className='payment-summary-item'>
                                                        <span className='payment-summary-label'>Pending Amount</span>
                                                        <span className='payment-summary-value text-warning'>
                                                            {orderDetails.paymentSummary.formattedPendingAmount}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className='col-md-3'>
                                                    <div className='payment-summary-item'>
                                                        <span className='payment-summary-label'>Remaining Amount</span>
                                                        <span className='payment-summary-value text-danger'>
                                                            {orderDetails.paymentSummary.formattedRemainingAmount}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className='col-md-3'>
                                                    <div className='payment-summary-item'>
                                                        <span className='payment-summary-label'>Suggested Amount</span>
                                                        <span className='payment-summary-value text-primary fw-bold'>
                                                            {PaymentReceiveService.formatCurrency(orderDetails.paymentSummary.remainingAmount)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Payment Form */}
                    <div className='payment-form-section'>
                        <h4 className='section-title'>
                            <i className='fas fa-credit-card me-2 text-primary'></i>
                            Payment Details
                        </h4>
                        
                        <form onSubmit={handleSubmit}>
                            <div className='row'>
                                <div className='col-md-6'>
                                    <div className='form-group'>
                                        <label className='form-label'>
                                            Order ID <span className='text-danger'>*</span>
                                        </label>
                                        <input
                                            type='text'
                                            className={`form-control ${errors.orderId ? 'is-invalid' : ''}`}
                                            name='orderId'
                                            value={formData.orderId}
                                            onChange={handleInputChange}
                                            placeholder='Enter Order ID'
                                            disabled={!!orderId}
                                        />
                                        {errors.orderId && (
                                            <div className='invalid-feedback'>{errors.orderId}</div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className='col-md-6'>
                                    <div className='form-group'>
                                        <label className='form-label'>Invoice ID</label>
                                        <input
                                            type='text'
                                            className='form-control'
                                            name='invoiceId'
                                            value={formData.invoiceId}
                                            onChange={handleInputChange}
                                            placeholder='Enter Invoice ID (Optional)'
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className='row'>
                                <div className='col-md-6'>
                                    <div className='form-group'>
                                        <label className='form-label'>
                                            Amount <span className='text-danger'>*</span>
                                        </label>
                                        <div className='input-group'>
                                            <span className='input-group-text'>৳</span>
                                            <input
                                                type='number'
                                                className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                                                name='amount'
                                                value={formData.amount}
                                                onChange={handleInputChange}
                                                placeholder='0.00'
                                                step='0.01'
                                                min='0.01'
                                            />
                                        </div>
                                        {errors.amount && (
                                            <div className='invalid-feedback'>{errors.amount}</div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className='col-md-6'>
                                    <div className='form-group'>
                                        <label className='form-label'>
                                            Payment Type <span className='text-danger'>*</span>
                                        </label>
                                        <select
                                            className={`form-select ${errors.paymentType ? 'is-invalid' : ''}`}
                                            name='paymentType'
                                            value={formData.paymentType}
                                            onChange={handleInputChange}
                                        >
                                            <option value=''>Select Payment Type</option>
                                            {getPaymentTypes().map(type => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.paymentType && (
                                            <div className='invalid-feedback'>{errors.paymentType}</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className='row'>
                                <div className='col-md-6'>
                                    <div className='form-group'>
                                        <label className='form-label'>Transaction Mode</label>
                                        <select
                                            className='form-select'
                                            name='modeOfTransaction'
                                            value={formData.modeOfTransaction}
                                            onChange={handleInputChange}
                                        >
                                            <option value=''>Select Transaction Mode</option>
                                            {getTransactionModes().map(mode => (
                                                <option key={mode.value} value={mode.value}>
                                                    {mode.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                
                                <div className='col-md-6'>
                                    <div className='form-group'>
                                        <label className='form-label'>Transaction ID</label>
                                        <input
                                            type='text'
                                            className='form-control'
                                            name='transactionId'
                                            value={formData.transactionId}
                                            onChange={handleInputChange}
                                            placeholder='Enter Transaction ID (Optional)'
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className='row'>
                                <div className='col-md-6'>
                                    <div className='form-group'>
                                        <label className='form-label'>Notes</label>
                                        <textarea
                                            className='form-control'
                                            name='notes'
                                            value={formData.notes}
                                            onChange={handleInputChange}
                                            placeholder='Enter payment notes (Optional)'
                                            rows='3'
                                        ></textarea>
                                    </div>
                                </div>
                                
                                <div className='col-md-6'>
                                    <div className='form-group'>
                                        <label className='form-label'>Attach File</label>
                                        <input
                                            type='file'
                                            className={`form-control ${fileError ? 'is-invalid' : ''}`}
                                            onChange={handleFileChange}
                                            accept='.jpg,.jpeg,.png,.gif,.pdf,.doc,.docx'
                                        />
                                        {fileError && (
                                            <div className='invalid-feedback'>{fileError}</div>
                                        )}
                                        <div className='form-text'>
                                            Supported formats: JPEG, PNG, GIF, PDF, DOC, DOCX (Max 5MB)
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='form-actions'>
                                <button
                                    type='submit'
                                    className='btn btn-primary btn-lg'
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Creating Payment...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save me-2"></i>
                                            Receive Payment
                                        </>
                                    )}
                                </button>
                                
                                <Link to="/payments/receive" className='btn btn-secondary btn-lg ms-2'>
                                    <i className="fas fa-times me-2"></i>
                                    Cancel
                                </Link>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <style jsx>{`
                /* Card Header */
                .card-header-mobile {
                    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
                    border-bottom: 1px solid #e9ecef;
                    border-radius: 0.75rem 0.75rem 0 0 !important;
                    padding: 1.5rem;
                    position: relative;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }

                .card-title {
                    display: flex;
                    align-items: center;
                    margin: 0;
                }

                .card-label {
                    color: #212529;
                    font-weight: 600;
                    margin: 0;
                    font-size: 1.5rem;
                }

                .card-toolbar-mobile {
                    display: flex;
                    gap: 0.75rem;
                    align-items: center;
                }

                .btn-mobile {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    border-radius: 0.5rem;
                    font-weight: 500;
                    font-size: 0.875rem;
                    padding: 0.625rem 1.25rem;
                    border: none;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }

                .btn-mobile:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
                }

                /* Section Styling */
                .section-title {
                    color: #212529;
                    font-weight: 600;
                    margin-bottom: 1.5rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 2px solid #e9ecef;
                    font-size: 1.25rem;
                }

                .order-summary-section,
                .payment-form-section {
                    background: white;
                    border-radius: 0.75rem;
                    padding: 1.5rem;
                    border: 1px solid #e9ecef;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                    margin-bottom: 1.5rem;
                }

                /* Summary Cards */
                .summary-card {
                    background: #f8f9fa;
                    border-radius: 0.75rem;
                    padding: 1.25rem;
                    border: 1px solid #e9ecef;
                    height: 100%;
                    transition: all 0.3s ease;
                }

                .summary-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    border-color: #007bff;
                }

                .summary-card-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 1rem;
                }

                .summary-card-header i {
                    font-size: 1.5rem;
                }

                .summary-card-header h5 {
                    margin: 0;
                    font-weight: 600;
                    color: #212529;
                    font-size: 1.1rem;
                }

                .summary-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.75rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 1px solid #e9ecef;
                }

                .summary-item:last-child {
                    margin-bottom: 0;
                    padding-bottom: 0;
                    border-bottom: none;
                }

                .summary-label {
                    font-weight: 600;
                    color: #495057;
                    font-size: 0.875rem;
                }

                .summary-value {
                    color: #212529;
                    font-weight: 500;
                }

                /* Payment Summary Card */
                .payment-summary-card {
                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                    border-radius: 0.75rem;
                    padding: 1.25rem;
                    border: 1px solid #dee2e6;
                }

                .payment-summary-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 1rem;
                }

                .payment-summary-header i {
                    font-size: 1.5rem;
                }

                .payment-summary-header h5 {
                    margin: 0;
                    font-weight: 600;
                    color: #212529;
                    font-size: 1.1rem;
                }

                .payment-summary-item {
                    text-align: center;
                    padding: 0.75rem;
                }

                .payment-summary-label {
                    display: block;
                    font-weight: 600;
                    color: #495057;
                    font-size: 0.875rem;
                    margin-bottom: 0.5rem;
                }

                .payment-summary-value {
                    display: block;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                /* Form Styling */
                .form-group {
                    margin-bottom: 1.5rem;
                }

                .form-label {
                    font-weight: 600;
                    color: #495057;
                    margin-bottom: 0.5rem;
                    font-size: 0.875rem;
                }

                .form-control,
                .form-select {
                    border-radius: 0.5rem;
                    border: 2px solid #e9ecef;
                    padding: 0.75rem;
                    font-size: 0.875rem;
                    transition: all 0.3s ease;
                }

                .form-control:focus,
                .form-select:focus {
                    border-color: #007bff;
                    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.15);
                    outline: none;
                }

                .form-control.is-invalid,
                .form-select.is-invalid {
                    border-color: #dc3545;
                }

                .invalid-feedback {
                    font-size: 0.8rem;
                    margin-top: 0.25rem;
                }

                .form-text {
                    font-size: 0.8rem;
                    color: #6c757d;
                    margin-top: 0.25rem;
                }

                .input-group-text {
                    background: #f8f9fa;
                    border: 2px solid #e9ecef;
                    border-right: none;
                    color: #495057;
                    font-weight: 600;
                }

                .input-group .form-control {
                    border-left: none;
                }

                /* Form Actions */
                .form-actions {
                    margin-top: 2rem;
                    padding-top: 1.5rem;
                    border-top: 1px solid #e9ecef;
                    text-align: center;
                }

                .btn-lg {
                    padding: 0.875rem 2rem;
                    font-size: 1rem;
                    font-weight: 600;
                    border-radius: 0.5rem;
                    transition: all 0.3s ease;
                }

                .btn-lg:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
                }

                /* Responsive Design */
                @media (max-width: 991.98px) {
                    .card-header-mobile {
                        padding: 1rem;
                        flex-direction: column;
                        align-items: stretch;
                        gap: 1rem;
                    }

                    .card-title {
                        justify-content: center;
                    }

                    .card-label {
                        font-size: 1.25rem;
                    }

                    .card-toolbar-mobile {
                        width: 100%;
                        justify-content: center;
                        gap: 0.5rem;
                    }

                    .btn-mobile {
                        flex: 1;
                        justify-content: center;
                        padding: 0.75rem 1rem;
                        font-size: 0.875rem;
                        min-height: 2.5rem;
                    }

                    .order-summary-section,
                    .payment-form-section {
                        padding: 1rem;
                        margin-bottom: 1rem;
                    }

                    .section-title {
                        font-size: 1.1rem;
                        margin-bottom: 1rem;
                    }

                    .summary-card,
                    .payment-summary-card {
                        padding: 1rem;
                        margin-bottom: 1rem;
                    }

                    .summary-card-header,
                    .payment-summary-header {
                        margin-bottom: 0.75rem;
                    }

                    .summary-card-header h5,
                    .payment-summary-header h5 {
                        font-size: 1rem;
                    }

                    .form-group {
                        margin-bottom: 1rem;
                    }

                    .payment-summary-item {
                        padding: 0.5rem;
                    }

                    .payment-summary-value {
                        font-size: 1rem;
                    }
                }

                @media (max-width: 767.98px) {
                    .card-header-mobile {
                        padding: 0.75rem;
                        border-radius: 0.5rem 0.5rem 0 0 !important;
                    }

                    .card-label {
                        font-size: 1.1rem;
                        text-align: center;
                    }

                    .btn-mobile {
                        padding: 0.625rem 0.75rem;
                        font-size: 0.8rem;
                        min-height: 2.25rem;
                    }

                    .btn-mobile .btn-text {
                        font-size: 0.8rem;
                    }

                    .order-summary-section,
                    .payment-form-section {
                        padding: 0.75rem;
                        border-radius: 0.5rem;
                    }

                    .section-title {
                        font-size: 1rem;
                        margin-bottom: 0.75rem;
                        padding-bottom: 0.5rem;
                    }

                    .summary-card,
                    .payment-summary-card {
                        padding: 0.75rem;
                        border-radius: 0.5rem;
                    }

                    .summary-card-header,
                    .payment-summary-header {
                        margin-bottom: 0.5rem;
                    }

                    .summary-card-header i,
                    .payment-summary-header i {
                        font-size: 1.25rem;
                    }

                    .summary-card-header h5,
                    .payment-summary-header h5 {
                        font-size: 0.9rem;
                    }

                    .summary-item {
                        margin-bottom: 0.5rem;
                        padding-bottom: 0.5rem;
                    }

                    .summary-label {
                        font-size: 0.8rem;
                    }

                    .summary-value {
                        font-size: 0.85rem;
                    }

                    .form-label {
                        font-size: 0.8rem;
                    }

                    .form-control,
                    .form-select {
                        padding: 0.625rem;
                        font-size: 0.8rem;
                        border-radius: 0.375rem;
                    }

                    .payment-summary-item {
                        padding: 0.375rem;
                    }

                    .payment-summary-label {
                        font-size: 0.8rem;
                    }

                    .payment-summary-value {
                        font-size: 0.9rem;
                    }

                    .form-actions {
                        margin-top: 1.5rem;
                        padding-top: 1rem;
                    }

                    .btn-lg {
                        padding: 0.75rem 1.5rem;
                        font-size: 0.9rem;
                    }
                }

                @media (max-width: 575.98px) {
                    .card-header-mobile {
                        padding: 0.5rem;
                    }

                    .card-label {
                        font-size: 1rem;
                    }

                    .btn-mobile {
                        padding: 0.5rem 0.625rem;
                        font-size: 0.75rem;
                        min-height: 2rem;
                    }

                    .btn-mobile i {
                        font-size: 0.75rem;
                    }

                    .order-summary-section,
                    .payment-form-section {
                        padding: 0.5rem;
                    }

                    .section-title {
                        font-size: 0.9rem;
                        margin-bottom: 0.5rem;
                        padding-bottom: 0.375rem;
                    }

                    .summary-card,
                    .payment-summary-card {
                        padding: 0.5rem;
                    }

                    .summary-card-header,
                    .payment-summary-header {
                        margin-bottom: 0.375rem;
                    }

                    .summary-card-header i,
                    .payment-summary-header i {
                        font-size: 1rem;
                    }

                    .summary-card-header h5,
                    .payment-summary-header h5 {
                        font-size: 0.85rem;
                    }

                    .summary-item {
                        margin-bottom: 0.375rem;
                        padding-bottom: 0.375rem;
                    }

                    .summary-label {
                        font-size: 0.75rem;
                    }

                    .summary-value {
                        font-size: 0.8rem;
                    }

                    .form-group {
                        margin-bottom: 0.75rem;
                    }

                    .form-label {
                        font-size: 0.75rem;
                        margin-bottom: 0.25rem;
                    }

                    .form-control,
                    .form-select {
                        padding: 0.5rem;
                        font-size: 0.75rem;
                        border-radius: 0.25rem;
                    }

                    .payment-summary-item {
                        padding: 0.25rem;
                    }

                    .payment-summary-label {
                        font-size: 0.75rem;
                        margin-bottom: 0.25rem;
                    }

                    .payment-summary-value {
                        font-size: 0.8rem;
                    }

                    .form-actions {
                        margin-top: 1rem;
                        padding-top: 0.75rem;
                    }

                    .btn-lg {
                        padding: 0.625rem 1.25rem;
                        font-size: 0.8rem;
                    }
                }
            `}</style>
        </>
    )
}

export default PaymentReceiveCreate 